import { useEffect, useState } from "react";
import {
  Plus,
  Pencil,
  TrashSimple,
  Eye,
  EyeSlash,
  CircleNotch,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY = {
  title: "",
  excerpt: "",
  content: "",
  cover_image_url: "",
  status: "draft",
  tags: "",
};

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | post
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/posts");
      setPosts(r.data.posts || []);
    } catch {
      toast.error("Could not load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const open = (p) => {
    if (p === "new") {
      setForm(EMPTY);
      setEditing("new");
    } else {
      setForm({
        title: p.title || "",
        excerpt: p.excerpt || "",
        content: p.content || "",
        cover_image_url: p.cover_image_url || "",
        status: p.status || "draft",
        tags: (p.tags || []).join(", "),
      });
      setEditing(p);
    }
  };

  const close = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        content: form.content,
        cover_image_url: form.cover_image_url,
        status: form.status,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
      if (editing === "new") {
        await api.post("/admin/posts", payload);
        toast.success("Post created");
      } else {
        await api.patch(`/admin/posts/${editing.id}`, payload);
        toast.success("Post updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      setPosts((ps) => ps.filter((p) => p.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-posts-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
            Posts
          </p>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
            Content & announcements
          </h1>
        </div>
        <button
          data-testid="new-post-btn"
          onClick={() => open("new")}
          className="px-4 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth flex items-center gap-2"
        >
          <Plus size={16} weight="bold" /> New post
        </button>
      </div>

      <div className="mt-8 mc-card overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center gap-3 text-[#4A5568]">
            <CircleNotch className="animate-spin" /> Loading posts...
          </div>
        ) : posts.length === 0 ? (
          <div className="p-10 text-center text-[#4A5568]">
            No posts yet. Create your first announcement.
          </div>
        ) : (
          <ul className="divide-y divide-[#E8DFD1]">
            {posts.map((p) => (
              <li
                key={p.id}
                className="px-5 py-4 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row"
                data-testid={`post-${p.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg text-[#0A1128] truncate">
                      {p.title}
                    </h3>
                    {p.status === "published" ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#16A34A]/10 text-[#16A34A] flex items-center gap-1">
                        <Eye size={10} weight="bold" /> Published
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F5EFE6] text-[#4A5568] flex items-center gap-1">
                        <EyeSlash size={10} weight="bold" /> Draft
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#4A5568] mt-0.5 truncate">
                    /{p.slug} · {new Date(p.created_at).toLocaleDateString()}
                  </p>
                  {p.excerpt && (
                    <p className="text-sm text-[#0A1128]/80 mt-1 line-clamp-1">
                      {p.excerpt}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`edit-${p.id}`}
                    onClick={() => open(p)}
                    className="px-3 py-1.5 rounded-md border border-[#E8DFD1] text-sm hover:border-[#3B5BDB] smooth flex items-center gap-1"
                  >
                    <Pencil size={14} /> Edit
                  </button>
                  <button
                    data-testid={`delete-${p.id}`}
                    onClick={() => remove(p.id)}
                    className="px-3 py-1.5 rounded-md border border-[#E8DFD1] text-sm hover:border-[#DC2626] hover:text-[#DC2626] smooth"
                  >
                    <TrashSimple size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-[#0A1128]/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto"
          onClick={(e) => e.target === e.currentTarget && close()}
          data-testid="post-modal"
        >
          <div className="bg-white rounded-2xl border border-[#E8DFD1] w-full max-w-2xl my-auto">
            <div className="px-6 py-4 border-b border-[#E8DFD1] flex items-center justify-between">
              <h2 className="font-display text-xl text-[#0A1128]">
                {editing === "new" ? "New post" : "Edit post"}
              </h2>
              <button onClick={close} className="text-[#4A5568] hover:text-[#0A1128]">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <Field label="Title">
                <input
                  data-testid="post-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                />
              </Field>
              <Field label="Excerpt (1-2 lines, shown in lists)">
                <input
                  data-testid="post-excerpt"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                />
              </Field>
              <Field label="Content (Markdown supported)">
                <textarea
                  data-testid="post-content"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={10}
                  className="w-full px-3 py-2 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] font-body text-sm"
                />
              </Field>
              <Field label="Cover image URL (optional)">
                <input
                  data-testid="post-cover"
                  value={form.cover_image_url}
                  onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                  className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  placeholder="https://..."
                />
              </Field>
              <Field label="Tags (comma separated)">
                <input
                  data-testid="post-tags"
                  value={form.tags}
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
                  placeholder="news, hallmark"
                />
              </Field>
              <Field label="Status">
                <div className="flex gap-2">
                  {["draft", "published"].map((s) => (
                    <button
                      key={s}
                      data-testid={`status-${s}`}
                      onClick={() => setForm({ ...form, status: s })}
                      className={`px-4 py-2 rounded-md border text-sm capitalize smooth ${
                        form.status === s
                          ? "border-[#3B5BDB] bg-[#3B5BDB]/5 text-[#3B5BDB] font-medium"
                          : "border-[#E8DFD1]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="px-6 py-4 border-t border-[#E8DFD1] flex justify-end gap-2">
              <button
                onClick={close}
                className="px-4 py-2 rounded-md border border-[#E8DFD1] text-sm hover:border-[#0A1128] smooth"
              >
                Cancel
              </button>
              <button
                data-testid="post-save"
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded-md bg-[#3B5BDB] text-white text-sm font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60"
              >
                {saving ? "Saving..." : editing === "new" ? "Create post" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#4A5568]">
        {label}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
